import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";

import { CompaniesWrapperStyles } from "pages/Companies/styled";
import { CommonDashboardWrapper } from "styles/GlobalStyles";
import {
  CardFlexWrapperStyles,
  CardWrapperStyles,
  TableWrapperStyles
} from "pages/TalentUploadRequest/styled";

import PillButtons from "components/Buttons/PillButtons";
import CommonTable from "components/Tables/CommonTable";
import Input from "components/Input";
import LoadingSpinner from "components/PageLoader/Spinner";
import SkeletonLoader from "components/PageLoader/SkeletonLoader";

import columns from "pages/Companies/column";
import { useCompaniesSlice } from "pages/Companies/slice/index";
import {
  selectCompanies,
  selectCompanyInfoDetails
} from "pages/Companies/slice/selector";

import Icon from "components/Icon/Icon";

import {
  DotIcon,
  CaretDownIcon,
  XIcon,
  MagnifyingGlassIcon
} from "@phosphor-icons/react";
import Select from "components/Select/index";
import { searchFieldCompanyOptions } from "./data";

const Companies = () => {
  const dispatch = useDispatch();
  const { actions } = useCompaniesSlice();
  const isInitialMount = useRef(true);
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize filters from URL
  const initialSearch = searchParams.get("search") || "";
  const initialStatus = searchParams.get("status") || "";
  const initialSortBy = searchParams.get("sort_by") || "";
  const initialSortOrder = searchParams.get("sort_order") || "";

  const [selectedStatus, setSelectedStatus] = useState(
    initialStatus === "verified"
      ? "Verified"
      : initialStatus === "unverified"
        ? "Unverified"
        : initialStatus === "no_status_companies"
          ? "No Status"
          : ""
  );

  const [searchTerm, setSearchTerm] = useState(initialSearch);
  const [debouncedSearch, setDebouncedSearch] = useState(initialSearch);
  const [sortBy, setSortBy] = useState(initialSortBy);
  const [sortOrder, setSortOrder] = useState(initialSortOrder);
  const [searchField, setSearchField] = useState("domain");

  const {
    initialLoading,
    loading,
    companies,
    filtered_count,
    pagination,
    total_companies,
    unverified_companies,
    verified_companies,
    no_status_companies
  } = useSelector(selectCompanies);
  const infoDetails = useSelector(selectCompanyInfoDetails);

  const statusMap = {
    Verified: "verified",
    Unverified: "unverified",
    "No Status": "no_status_companies"
  };
  const currentStatus = statusMap[selectedStatus];
  const totalCountForStatus =
    debouncedSearch || currentStatus ? filtered_count : total_companies;

  // Status dropdown items
  const statusMenuItems = [
    { label: "Verified", value: "verified", count: verified_companies },
    { label: "Unverified", value: "unverified", count: unverified_companies },
    {
      label: "No Status",
      value: "no_status_companies",
      count: no_status_companies
    }
  ];

  // Sync filters, search, sort, page to URL
  const syncURL = (page = 1, statusValue = currentStatus) => {
    const params = { page };
    if (debouncedSearch) params.search = debouncedSearch;
    if (statusValue) params.status = statusValue;
    if (sortBy) params.sort_by = sortBy;
    if (sortOrder) params.sort_order = sortOrder;
    setSearchParams(params);
  };

  // Fetch companies whenever URL changes
  useEffect(() => {
    const pageFromURL = Number(searchParams.get("page")) || 1;
    const searchFromURL = searchParams.get("search") || "";
    const statusFromURL = searchParams.get("status") || "";
    const sortByFromURL = searchParams.get("sort_by") || "";
    const sortOrderFromURL = searchParams.get("sort_order") || "";

    dispatch(
      actions.fetchCompanies({
        page: pageFromURL,
        limit: pagination?.limit || 10,
        status: statusFromURL,
        search: searchFromURL,
        search_field: searchField,
        sort_by: sortByFromURL,
        sort_order: sortOrderFromURL
      })
    );
  }, [searchParams]);

  // Fetch company info only once
  useEffect(() => {
    if (!infoDetails.size.length) {
      dispatch(actions.fetchCompanyInfoDetails());
    }
  }, [dispatch, actions, infoDetails.size.length]);

  // Debounce search
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(searchTerm), 500);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  // Reset page when search changes (skip initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    syncURL(1);
  }, [debouncedSearch]);

  // Handlers
  const handleStatusSelect = (item) => {
    setSelectedStatus(item.label);
    syncURL(1, item.value); // send lowercase value
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setDebouncedSearch("");
    syncURL(1); // reset to page 1
  };

  const handleSortChange = (column, order) => {
    setSortBy(column);
    setSortOrder(order);
    syncURL(Number(searchParams.get("page")) || 1);
  };

  const handlePageChange = (newPage) => syncURL(newPage);

  const tableColumns = useMemo(() => columns(), []);

  const selectedOption = searchFieldCompanyOptions.find(
    (opt) => opt.value === searchField
  );

  const dynamicPlaceholder = selectedOption
    ? `Search by ${selectedOption.label}`
    : "Search";

  return (
    <CompaniesWrapperStyles>
      <CommonDashboardWrapper>
        <div className="common-dashboard-wrapper">
          <h1 className="common-heading">Companies</h1>
        </div>

        <CardFlexWrapperStyles>
          <CardWrapperStyles>
            <div className="card-list">
              <Icon
                color="#1F1F1F"
                component={DotIcon}
                weight="fill"
                format="outline"
              />
              <span className="card-title">All</span>
            </div>
            {initialLoading ? (
              <SkeletonLoader width="40px" />
            ) : (
              <p className="card-count">{total_companies}</p>
            )}
          </CardWrapperStyles>

          <CardWrapperStyles>
            <div className="card-list">
              <Icon
                color="#3F9142"
                component={DotIcon}
                weight="fill"
                format="outline"
              />
              <span className="card-title">Verified</span>
            </div>
            {initialLoading ? (
              <SkeletonLoader width="40px" />
            ) : (
              <p className="card-count">{verified_companies}</p>
            )}
          </CardWrapperStyles>

          <CardWrapperStyles>
            <div className="card-list">
              <Icon
                color="#F5A000"
                component={DotIcon}
                weight="fill"
                format="outline"
              />
              <span className="card-title">Unverified</span>
            </div>
            {initialLoading ? (
              <SkeletonLoader width="40px" />
            ) : (
              <p className="card-count">{unverified_companies}</p>
            )}
          </CardWrapperStyles>

          {no_status_companies > 0 && (
            <CardWrapperStyles>
              <div className="card-list">
                <Icon
                  color="#4C4C4C"
                  component={DotIcon}
                  weight="fill"
                  format="outline"
                />
                <span className="card-title">No Status</span>
              </div>
              {initialLoading ? (
                <SkeletonLoader width="40px" />
              ) : (
                <p className="card-count">{no_status_companies}</p>
              )}
            </CardWrapperStyles>
          )}
        </CardFlexWrapperStyles>
      </CommonDashboardWrapper>

      <TableWrapperStyles>
        <div className="filter-bar">
          <PillButtons
            placeholderText="Status"
            statusPlaceholder="statusActive"
            mainLabelText={selectedStatus}
            rightIcon={
              <Icon
                component={CaretDownIcon}
                format="outline"
                weight="regular"
              />
            }
            activeRightIcon={
              <Icon component={XIcon} format="outline" weight="regular" />
            }
            menuItems={statusMenuItems}
            onSelect={handleStatusSelect}
            size="M"
            maxWidth="144px"
          />

          <div className="search-dropdown-styles">
            <Select
              size="S"
              selectWrapperClassName="search-select"
              options={searchFieldCompanyOptions}
              value={searchField}
              onChange={(value) => setSearchField(value)}
            />

            <Input
              id="searchCompany"
              type="search"
              placeholder={dynamicPlaceholder}
              leftIcon={
                <Icon
                  component={MagnifyingGlassIcon}
                  format="outline"
                  weight="regular"
                />
              }
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="S"
              className="search-input"
              rightIcon={
                loading ? (
                  <LoadingSpinner color="#1f1f1f" size={12} />
                ) : (
                  <Icon component={XIcon} format="outline" weight="regular" />
                )
              }
              handleClearSearch={handleClearSearch}
            />
          </div>
        </div>

        <CommonTable
          data={companies}
          columns={tableColumns}
          loading={loading}
          pagination={pagination}
          total_count={totalCountForStatus}
          initialLoading={initialLoading}
          page={Number(searchParams.get("page")) || 1}
          limit={pagination?.limit || 10}
          onPageChange={handlePageChange}
          currentStatus={currentStatus}
          sort_by={sortBy}
          sort_order={sortOrder}
          onSortChange={handleSortChange}
        />
      </TableWrapperStyles>
    </CompaniesWrapperStyles>
  );
};

export default Companies;
